import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "react-bootstrap-icons";
import useOneTapAuth from "@/hooks/useOneTapAuth";
import { MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL } from "@/constants/mixpanel.constants";
import SignUpContent from "@/components/common/SignUpCard/SignUpContent";
import logo from "@/assets/icons/WePrice-Secondaryicon-w.png";

/**
 * Styles for positioning the banner below the header (9svh) with padding
 * to account for the back button position (10svh)
 */
const BANNER_STICKY_STYLES: React.CSSProperties = {
    top: "9svh",
};

interface OneTapSignInBannerProps {
    show: boolean;
    onClose: () => void;
    redirectPath?: string;
    version?: string;
    onStepChange?: (step: 1 | 2) => void;
    location?: string;
    onExitComplete?: () => void; // Callback when exit animation completes
}

/**
 * OneTapSignInBanner component displays a dismissible banner for one-tap sign-in
 * Positioned below the header on mobile puzzle detail pages
 * @param props - Component props
 * @returns {JSX.Element} OneTapSignInBanner component
 */
const OneTapSignInBanner: React.FC<OneTapSignInBannerProps> = ({
    show,
    onClose,
    redirectPath = "/puzzle",
    version = "puzzle",
    onStepChange,
    location = MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL,
    onExitComplete,
}) => {
    const { shouldShow, handleGoogle, handleEmail } = useOneTapAuth({ redirectPath, version, onClose, location });
    const [step, setStep] = useState<1 | 2>(1);

    // Notify parent of initial step when banner is shown
    useEffect(() => {
        if (show && shouldShow) {
            onStepChange?.(step);
        }
    }, [show, step, onStepChange, shouldShow]);

    // Reset step after exit animation completes to avoid visual glitch
    const handleExitComplete = () => {
        setStep(1);
        onExitComplete?.(); // Notify parent that exit animation is complete
    };

    // Only unmount when the one-tap should never show (e.g. user authenticated or feature disabled).
    if (!shouldShow) {
        return null;
    }

    const handlePrimaryContinue = () => {
        // Move from lightweight intro to full signup in step 2
        setStep(2);
        onStepChange?.(2);
    };

    return (
        <AnimatePresence onExitComplete={handleExitComplete}>
            {show && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="tw-overflow-hidden tw-bg-[#111111] tw-w-full tw-sticky tw-z-[40]"
                    style={BANNER_STICKY_STYLES}
                >
                    <div className="tw-p-3">
                        {step === 1 ? (
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-white">
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <div className="tw-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-full tw-bg-white/5">
                                        <img src={logo} alt="PriceMe logo" className="tw-h-6 tw-w-6" />
                                    </div>
                                    <div className="tw-flex tw-flex-col tw-gap-0.5">
                                        <span className="tw-font-semibold tw-text-base">Guest Mode</span>
                                        <span className="tw-text-sm tw-text-[#858585] tw-whitespace-nowrap">
                                            Sign in to track your stats
                                        </span>
                                    </div>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    {/* Primary action: Sign in button - receives focus first in tab order */}
                                    <button
                                        onClick={handlePrimaryContinue}
                                        tabIndex={0}
                                        className="tw-flex tw-items-center tw-justify-center tw-rounded-[5px] tw-bg-[#2f80ff] tw-px-3 tw-py-1 tw-text-sm tw-font-semibold hover:tw-opacity-90 tw-transition tw-whitespace-nowrap"
                                    >
                                        Sign in
                                    </button>
                                    {/* Secondary action: Close button - receives focus after primary action */}
                                    <button
                                        onClick={onClose}
                                        tabIndex={0}
                                        className="tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-full tw-text-gray-400 hover:tw-text-gray-200 tw-transition-colors"
                                        aria-label="Close"
                                    >
                                        <X className="tw-w-5 tw-h-5" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="tw-relative tw-mt-1">
                                {/* Close button positioned last in tab order for step 2 */}
                                <button
                                    onClick={onClose}
                                    tabIndex={0}
                                    className="tw-absolute tw-top-0 tw-right-0 tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-full tw-text-gray-400 hover:tw-text-gray-200 tw-transition-colors tw-z-10"
                                    aria-label="Close"
                                >
                                    <X className="tw-w-5 tw-h-5" />
                                </button>
                                <SignUpContent onGoogle={handleGoogle} onEmail={handleEmail} showTerms variant="dark" />
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default OneTapSignInBanner;
